import "./App.css";
import {
  CognitoAuthProvider,
  AppLayout,
  AccountMenu,
  LogoutPage,
  LoggedOutPage,
  ProtectedRoute,
  CallbackPage,
  AppFooter,
  AppBar,
  NavRail,
  ActiveOrgProvider,
  OrgSwitcher,
  UserDisplayName,
  BuildBadge,
  UsaBadge,
  installServerErrorInterceptor,
} from "common-fe";
import { createBrandTheme, Logo, colors } from "brand";

installServerErrorInterceptor();
import { OrganizationApi, Configuration } from "account-common";
import { useAuth } from "react-oidc-context";
import { useCallback } from "react";
import ShowListPage from "./pages/ShowListPage";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Inject the org-list fetcher (built from the generated OpenAPI client) into the
// shared ActiveOrgProvider, which never calls fetch itself. The org list is
// served by the ACCOUNT API, so manager-fe uses account-common's client.
const ManagerActiveOrgProvider = ({ children }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const listOrgs = useCallback(
    async (isSuper) => {
      if (!accessToken) return [];
      const api = new OrganizationApi(
        new Configuration({ basePath: ACCOUNT_API_BASE_URL, accessToken })
      );
      const res = isSuper
        ? await api.listAllOrganizations(true) // enabledOnly (#2074): switcher shows only superadminEnabled orgs
        : await api.listOrganizations();
      return res.data?.status === "success" && Array.isArray(res.data.data)
        ? res.data.data
        : [];
    },
    [accessToken]
  );
  return (
    <ActiveOrgProvider
      listOrgs={listOrgs}
      accountApiBasePath={ACCOUNT_API_BASE_URL}
    >
      {children}
    </ActiveOrgProvider>
  );
};
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ThemeProvider, useColorScheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import { useTranslation } from "i18n-fe";
import ShowDetailPage from "./pages/ShowDetailPage";
import ResultsPage from "./pages/ResultsPage";
import ResultGroupsPage from "./pages/ResultGroupsPage";
import UploadResultsPage from "./pages/UploadResultsPage";
import UploadHistoryPage from "./pages/UploadHistoryPage";

function AppContent({ cognitoAuthConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useColorScheme();
  const { t } = useTranslation("common");

  const handleThemeToggle = () => {
    const newMode = mode === "dark" ? "light" : "dark";
    setMode(newMode);
  };

  const theme = createBrandTheme();

  const handleLogout = () => {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const cognitoDomain = import.meta.env.VITE_COGNITO_AUTH_DOMAIN;
    const logoutUri = import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI;

    if (clientId && cognitoDomain && logoutUri) {
      const url = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
        logoutUri
      )}`;
      window.location.replace(url);
    }
  };

  const BrandLogo = () => (
    <Box
      onClick={() => navigate("/")}
      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
    >
      <Logo
        style={{
          paddingLeft: "8px",
          height: 40,
          width: "auto",
          minHeight: 40,
          minWidth: 40,
          maxHeight: 40,
          maxWidth: "none",
          marginRight: 16,
          flexShrink: 0,
          flexGrow: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );

  const accountMenuItems = [
    {
      icon: <HomeIcon />,
      text: t("navigation.home"),
      onClick: () => navigate("/"),
    },
    {
      icon: <AccountCircleIcon />,
      text: t("navigation.account"),
      href: import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings",
    },
    {
      icon:
        mode === "dark" ? (
          <Brightness7Icon fontSize="small" />
        ) : (
          <Brightness4Icon fontSize="small" />
        ),
      text:
        mode === "dark"
          ? t("navigation.theme.light")
          : t("navigation.theme.dark"),
      onClick: handleThemeToggle,
    },
    {
      icon: <LogoutIcon />,
      text: t("navigation.logout"),
      onClick: handleLogout,
    },
  ];

  const navItems = [
    // {
    //   icon: <HomeIcon />,
    //   text: t("navigation.home"),
    //   onClick: () => navigate("/"),
    // },
    {
      icon: <ContentPasteGoIcon />,
      text: t("navigation.results"),
      onClick: () => navigate("/results"),
    },
  ];

  const appBarRightContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <OrgSwitcher accountOrgsUrl={`${import.meta.env.VITE_ACCOUNT_FE_BASE_URL}/orgs`} />
      <UserDisplayName />
      <AccountMenu menuItems={accountMenuItems} />
    </Box>
  );

  return (
    <CognitoAuthProvider
      cognitoAuthConfig={cognitoAuthConfig}
      location={location}
    >
      <ThemeProvider theme={theme} defaultMode="light">
        <CssBaseline />
        <ManagerActiveOrgProvider>
        <AppLayout
          appBar={
            <AppBar
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
              rightContent={appBarRightContent}
              brandLogo={<BrandLogo />}
            />
          }
          navRail={
            <NavRail
              navItems={navItems}
              backgroundColor={colors.darkGrey}
              color="#ffffff"
            />
          }
          appFooterEnabled={true}
          appFooter={
            <AppFooter
              sx={{ backgroundColor: colors.darkGrey, color: "#ffffff" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">
                  {t("app.copyright", { year: new Date().getFullYear() })}
                </Typography>
              </Box>
            </AppFooter>
          }
          brandLogo={<BrandLogo />}
          appBarRightContent={appBarRightContent}
        >
          <Routes>
            <Route
              path="/callback"
              element={
                <CallbackPage navigateFunction={navigate} location={location} />
              }
            />
            <Route
              path="/logout"
              element={<LogoutPage navigateFunction={navigate} />}
            />
            <Route path="/logged-out" element={<LoggedOutPage />} />

            {/* <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ShowListPage />
                </ProtectedRoute>
              }
            /> */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shows"
              element={
                <ProtectedRoute>
                  <ShowListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shows/:showId"
              element={
                <ProtectedRoute>
                  <ShowDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results"
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results/upload"
              element={
                <ProtectedRoute>
                  <UploadResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results/:compYear/:compid"
              element={
                <ProtectedRoute>
                  <ResultGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results/:compYear/:compid/uploads"
              element={
                <ProtectedRoute>
                  <UploadHistoryPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AppLayout>
        </ManagerActiveOrgProvider>
      </ThemeProvider>
    </CognitoAuthProvider>
  );
}

function App({ cognitoAuthConfig }) {
  const theme = createBrandTheme();

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AppContent cognitoAuthConfig={cognitoAuthConfig} />
      <UsaBadge />
      <BuildBadge />
    </ThemeProvider>
  );
}

export default App;
